import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  AccountBalanceWallet,
  People,
  Person,
  AttachMoney,
} from "@mui/icons-material";

const PaymentStrategyDialog = ({
  open,
  onClose,
  orderData,
  onPaymentInitiated,
  currentUserId,
}) => {
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculate upfront amounts for each strategy
  const calculateAmounts = () => {
    if (!orderData?.groupOrder?.userOrders) {
      return {
        individualAmounts: [],
        totalUpfront: 0,
        splitEvenlyAmount: 0,
        activeUsersCount: 0,
      };
    }

    const userOrders = orderData.groupOrder.userOrders;
    const upfrontPercentage = orderData.paymentSettings?.upfrontPercentage || 20;
    
    // Calculate individual amounts
    const individualAmounts = userOrders.map(userOrder => ({
      userId: userOrder.userId,
      userName: userOrder.userFullName,
      amount: ((userOrder.subtotal || 0) * upfrontPercentage) / 100,
      subtotal: userOrder.subtotal || 0,
    }));

    // Calculate total upfront amount
    const totalUpfront = individualAmounts.reduce((sum, user) => sum + (user.amount || 0), 0);

    // Calculate split evenly amount (only for users who haven't rejected)
    const activeUsers = userOrders.filter(user => 
      user.paymentType !== "rejected" && user.paymentType !== "split_per_person"
    );
    const splitEvenlyAmount = activeUsers.length > 0 ? totalUpfront / activeUsers.length : 0;

    return {
      individualAmounts,
      totalUpfront,
      splitEvenlyAmount,
      activeUsersCount: activeUsers.length,
    };
  };

  const amounts = calculateAmounts();

  // Timer effect
  useEffect(() => {
    if (open && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open, timeRemaining]);

  // Reset timer when dialog opens
  useEffect(() => {
    if (open) {
      setTimeRemaining(30);
      setSelectedStrategy("");
    }
  }, [open]);

  const handleStrategyChange = (event) => {
    setSelectedStrategy(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedStrategy) return;

    setIsProcessing(true);
    try {
      // Prepare payment initiation data
      const paymentData = {
        orderId: orderData._id,
        strategy: selectedStrategy,
        initiatorId: currentUserId,
        amounts: amounts,
        expiresAt: new Date(Date.now() + 30000), // 30 seconds from now
      };

      // Call the payment initiation handler
      if (onPaymentInitiated) {
        await onPaymentInitiated(paymentData);
      }

      onClose();
    } catch (error) {
      console.error("Error initiating payment:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStrategyDescription = (strategy) => {
    switch (strategy) {
      case "pay_for_all":
        return "You will pay the upfront amount for all users. Others will get an accept/reject request.";
      case "split_evenly":
        return "The upfront amount will be split evenly among all users. Others will get an accept/reject request.";
      case "split_per_person":
        return "Each user pays only for their own items. No requests will be sent to others.";
      default:
        return "";
    }
  };

  const getStrategyAmount = (strategy) => {
    switch (strategy) {
      case "pay_for_all":
        return amounts.totalUpfront || 0;
      case "split_evenly":
        return amounts.splitEvenlyAmount || 0;
      case "split_per_person":
        const currentUserOrder = amounts.individualAmounts?.find(
          user => user.userId === currentUserId
        );
        return currentUserOrder?.amount || 0;
      default:
        return 0;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 1,
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
          color: "white",
          fontWeight: 700,
          fontSize: "1.5rem",
        }}
      >
        Choose Payment Strategy
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Timer */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Time remaining:
            </Typography>
            <Chip
              label={`${timeRemaining}s`}
              color={timeRemaining <= 10 ? "error" : "primary"}
              size="small"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={(timeRemaining / 30) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "#E5E7EB",
              "& .MuiLinearProgress-bar": {
                backgroundColor: timeRemaining <= 10 ? "#EF4444" : "#7C3AED",
              },
            }}
          />
        </Box>

        {/* Upfront percentage info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#F3F4F6", borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Upfront payment required: {orderData?.paymentSettings?.upfrontPercentage || 20}%
          </Typography>
        </Box>

        {/* Strategy options */}
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <RadioGroup
            value={selectedStrategy}
            onChange={handleStrategyChange}
            sx={{ gap: 2 }}
          >
            {/* Pay for All */}
            <Card
              sx={{
                border: selectedStrategy === "pay_for_all" ? "2px solid #7C3AED" : "1px solid #E5E7EB",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#7C3AED",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.15)",
                },
              }}
            >
              <FormControlLabel
                value="pay_for_all"
                control={<Radio />}
                label=""
                sx={{ m: 0, width: "100%" }}
              />
              <CardContent sx={{ pt: 1, pb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AccountBalanceWallet sx={{ color: "#7C3AED", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Pay for All
                  </Typography>
                  <Box sx={{ ml: "auto" }}>
                    <Typography variant="h6" sx={{ color: "#7C3AED", fontWeight: 700 }}>
                      ${getStrategyAmount("pay_for_all").toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {getStrategyDescription("pay_for_all")}
                </Typography>
              </CardContent>
            </Card>

            {/* Split Evenly */}
            <Card
              sx={{
                border: selectedStrategy === "split_evenly" ? "2px solid #7C3AED" : "1px solid #E5E7EB",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#7C3AED",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.15)",
                },
              }}
            >
              <FormControlLabel
                value="split_evenly"
                control={<Radio />}
                label=""
                sx={{ m: 0, width: "100%" }}
              />
              <CardContent sx={{ pt: 1, pb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <People sx={{ color: "#7C3AED", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Split Evenly
                  </Typography>
                  <Box sx={{ ml: "auto" }}>
                    <Typography variant="h6" sx={{ color: "#7C3AED", fontWeight: 700 }}>
                      ${getStrategyAmount("split_evenly").toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {getStrategyDescription("split_evenly")}
                </Typography>
              </CardContent>
            </Card>

            {/* Split Per Person */}
            <Card
              sx={{
                border: selectedStrategy === "split_per_person" ? "2px solid #7C3AED" : "1px solid #E5E7EB",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#7C3AED",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.15)",
                },
              }}
            >
              <FormControlLabel
                value="split_per_person"
                control={<Radio />}
                label=""
                sx={{ m: 0, width: "100%" }}
              />
              <CardContent sx={{ pt: 1, pb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Person sx={{ color: "#7C3AED", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    split per person
                  </Typography>
                  <Box sx={{ ml: "auto" }}>
                    <Typography variant="h6" sx={{ color: "#7C3AED", fontWeight: 700 }}>
                      ${getStrategyAmount("split_per_person").toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {getStrategyDescription("split_per_person")}
                </Typography>
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ mr: 2, textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedStrategy || isProcessing || timeRemaining === 0}
          sx={{
            textTransform: "none",
            background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #6D28D9 0%, #9333EA 100%)",
            },
          }}
        >
          {isProcessing ? "Processing..." : "Proceed"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentStrategyDialog;
